import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Edition, EditionItem } from "@/types/newsletter";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import PageSkeleton from "@/components/PageSkeleton";
import SEOHead, { SITE_URL } from "@/components/SEOHead";
import logoFull from "@/assets/logo.png";
import logoIcon from "@/assets/logo-icon.png";
import { getReadingTime } from "@/lib/readingTime";
import { formatDeliveryTime, formatGenerationHourLocal } from "@/lib/formatDeliveryTime";
import { ChevronLeft, ChevronRight, Flame } from "lucide-react";

const PublicEdition = () => {
  const { id } = useParams<{ id: string }>();
  const [edition, setEdition] = useState<Edition | null>(null);
  const [items, setItems] = useState<EditionItem[]>([]);
  const [deliveredAt, setDeliveredAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [prevEdition, setPrevEdition] = useState<{ id: string; slug: string | null; hook: string | null } | null>(null);
  const [nextEdition, setNextEdition] = useState<{ id: string; slug: string | null; hook: string | null } | null>(null);

  useEffect(() => {
    const fetchEdition = async () => {
      if (!id) return;

      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
      const query = isUuid
        ? supabase.from("editions").select("*").eq("id", id).maybeSingle()
        : supabase.from("editions").select("*").eq("slug", id).maybeSingle();

      const { data: ed, error } = await query;

      if (error || !ed) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      // Fetch items, delivery time, prev/next editions in parallel
      const [itemsResult, deliveryResult, prevResult, nextResult] = await Promise.all([
        supabase
          .from("edition_items")
          .select("*")
          .eq("edition_id", ed.id)
          .order("item_order"),
        supabase.rpc("get_edition_delivered_at", { _edition_id: ed.id }),
        supabase
          .from("editions")
          .select("id, slug, hook")
          .in("status", ["approved", "sent"])
          .lt("edition_date", ed.edition_date)
          .order("edition_date", { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from("editions")
          .select("id, slug, hook")
          .in("status", ["approved", "sent"])
          .gt("edition_date", ed.edition_date)
          .order("edition_date", { ascending: true })
          .limit(1)
          .maybeSingle(),
      ]);

      setEdition(ed);
      setItems(itemsResult.data || []);
      setDeliveredAt(deliveryResult.data ?? null);
      setPrevEdition(prevResult.data ?? null);
      setNextEdition(nextResult.data ?? null);
      setLoading(false);
      window.scrollTo(0, 0);
    };
    fetchEdition();
  }, [id]);

  if (loading) {
    return <PageSkeleton />;
  }

  if (notFound || !edition) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center space-y-4">
          <h1 className="font-display text-2xl italic text-foreground">Edition not found</h1>
          <Link to="/">
            <Button variant="outline" className="rounded-none text-xs tracking-widest uppercase">
              Go Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const formattedDate = format(new Date(edition.edition_date + "T00:00:00"), "MMMM d, yyyy");

  // Keep page title under 60 chars for SERP. Suffix is " — aibly" (8 chars).
  const shortDate = format(new Date(edition.edition_date + "T00:00:00"), "MMM d");
  const buildTitle = (lead: string) => {
    const suffix = " — aibly";
    const max = 60;
    if (lead.length + suffix.length <= max) return `${lead}${suffix}`;
    return `${lead.slice(0, max - suffix.length - 1).trimEnd()}…${suffix}`;
  };
  const editionTitle = edition.hook
    ? buildTitle(edition.hook)
    : buildTitle(`AI News ${shortDate}`);

  // Dynamic meta description: hook + first item body snippet
  const firstItemSnippet = items[0]?.body?.slice(0, 120) || "";
  const editionDesc = edition.hook
    ? `${edition.hook}. ${firstItemSnippet}${firstItemSnippet.length >= 120 ? "…" : ""}`
    : edition.custom_message || `AI intelligence briefing for ${formattedDate}. ${items.length} stories covering the latest in artificial intelligence.`;

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={editionTitle}
        description={editionDesc.slice(0, 160)}
        canonical={`/edition/${edition.slug || edition.id}`}
        ogType="article"
        ogImage={`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/og-image?id=${edition.id}`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: edition.hook || `AI News ${formattedDate}`,
            datePublished: edition.edition_date,
            dateModified: edition.updated_at,
            author: { "@type": "Organization", name: "aibly" },
            publisher: {
              "@type": "Organization",
              name: "aibly",
              url: SITE_URL,
            },
            description: editionDesc.slice(0, 160),
            mainEntityOfPage: `${SITE_URL}/edition/${edition.slug || edition.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
              { "@type": "ListItem", position: 2, name: "Archive", item: `${SITE_URL}/archive` },
              { "@type": "ListItem", position: 3, name: edition.hook || `AI News ${formattedDate}`, item: `${SITE_URL}/edition/${edition.slug || edition.id}` },
            ],
          },
        ]}
      >
        <link rel="alternate" type="application/rss+xml" title="aibly RSS Feed" href={`${SITE_URL}/rss.xml`} />
      </SEOHead>

      {/* Minimal header */}
      <nav className="border-b border-border py-4 px-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={logoIcon} alt="aibly logo" width={500} height={500} className="h-16 w-auto dark:invert sm:hidden" />
            <img src={logoFull} alt="aibly logo" width={486} height={135} className="h-10 w-auto dark:invert hidden sm:block" />
          </Link>
          <Link to="/signup">
            <Button size="sm" className="rounded-none text-xs tracking-widest uppercase">
              Subscribe — Free
            </Button>
          </Link>
        </div>
      </nav>

      <article className="max-w-2xl mx-auto px-4 py-12">
        <div className="space-y-6">
          {/* Visible breadcrumb */}
          <nav className="text-xs text-muted-foreground tracking-widest uppercase" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <span className="mx-2">›</span>
            <Link to="/archive" className="hover:text-foreground transition-colors">Archive</Link>
            <span className="mx-2">›</span>
            <span className="text-foreground line-clamp-1 inline">{edition.hook || formattedDate}</span>
          </nav>

          <header className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-muted-foreground text-xs tracking-widest uppercase">
              <div className="w-2 h-2 rounded-full bg-accent animate-pulse-live" />
              <time dateTime={edition.edition_date}>{formattedDate}</time>
              {deliveredAt ? (
                <span>· {formatDeliveryTime(deliveredAt, Intl.DateTimeFormat().resolvedOptions().timeZone, "12h")}</span>
              ) : edition.generation_hour != null ? (
                <span>· {formatGenerationHourLocal(edition.generation_hour)}</span>
              ) : null}
              <span className="text-accent">⚡ {getReadingTime(edition, items)}</span>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent((edition.hook || "AI News") + " — via @aibly")}&url=${encodeURIComponent(`${SITE_URL}/share/${edition.id}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground transition-colors text-xs tracking-widest uppercase"
                title="Share on X"
                aria-label="Share this edition on X"
              >
                𝕏
              </a>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${SITE_URL}/share/${edition.id}`);
                  const btn = document.activeElement as HTMLElement;
                  if (btn) { btn.textContent = "✓"; setTimeout(() => { btn.textContent = "🔗"; }, 1500); }
                }}
                className="text-muted-foreground hover:text-foreground transition-colors text-xs tracking-widest uppercase"
                title="Copy link"
                aria-label="Copy edition share link"
              >
                🔗
              </button>
            </div>
          </header>

          {edition.hook && (
            <h1 className="font-display text-2xl md:text-3xl italic leading-tight text-foreground">
              {edition.hook}
            </h1>
          )}

          {edition.custom_message && (
            <div className="border-l-2 border-accent pl-4">
              <p className="text-muted-foreground text-sm italic">{edition.custom_message}</p>
            </div>
          )}

          <div className="space-y-5">
            {items.map((item, idx) => (
              <div key={item.id}>
                <div className="group pl-4 border-l border-border hover:border-muted-foreground transition-colors duration-300">
                  <p className="text-foreground/90 text-sm leading-relaxed">
                    &gt; {item.body}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    {item.is_trending && (
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase font-bold" style={{ color: "hsl(25 100% 50%)" }}>
                        <Flame className="w-3.5 h-3.5 animate-flame" />
                        Trending
                      </span>
                    )}
                    {item.source_name && (
                      <span className="text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-300 tracking-wider uppercase">
                        {item.source_url ? (
                          <a href={item.source_url} target="_blank" rel="noopener noreferrer">
                            [{item.source_name} →]
                          </a>
                        ) : (
                          `[${item.source_name}]`
                        )}
                      </span>
                    )}
                  </div>
                </div>
                {idx === 2 && items.length > 4 && (
                  <div className="my-6 border border-accent/30 p-4 text-center">
                    <p className="text-xs tracking-widest uppercase text-muted-foreground mb-2">
                      Enjoying this? Get it daily in your inbox.
                    </p>
                    <Link to="/signup">
                      <Button size="sm" className="rounded-none text-xs tracking-widest uppercase">
                        Subscribe Free
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Stat of the Day */}
          {edition.stat_number && (
            <div className="py-8 text-center border-y border-border">
              <h2 className="text-[10px] tracking-[0.4em] uppercase text-muted-foreground mb-3 font-mono">
                Stat of the Day
              </h2>
              <p className="font-display text-4xl md:text-5xl text-accent font-bold tracking-tight">
                {edition.stat_number}
              </p>
              {edition.stat_caption && (
                <p className="text-muted-foreground text-sm mt-3 max-w-md mx-auto">
                  {edition.stat_caption}
                </p>
              )}
            </div>
          )}

          {/* Quote of the Day */}
          {edition.quote_text && (
            <div className="py-6 px-6 text-center">
              <h2 className="text-[10px] tracking-[0.4em] uppercase text-muted-foreground mb-4 font-mono">
                Quote
              </h2>
              <blockquote className="font-display text-lg md:text-xl italic text-foreground leading-relaxed">
                "{edition.quote_text}"
              </blockquote>
              {edition.quote_attribution && (
                <p className="text-muted-foreground text-xs tracking-wider uppercase mt-3">
                  — {edition.quote_attribution}
                </p>
              )}
            </div>
          )}

          {edition.writers_take && (
            <div className="border-t border-accent pt-6 mt-8">
              <h2 className="text-accent text-[11px] tracking-[3px] uppercase font-mono mb-3">● Hot Take</h2>
              <div className="border-l-2 border-accent/50 pl-5">
                {edition.writers_take.split("\n").map((line: string, i: number) => (
                  <p
                    key={i}
                    className={`text-foreground/90 leading-relaxed mb-3 ${i === 0 ? "text-lg italic font-display" : "text-sm"}`}
                  >
                    {line}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* One More Thing */}
          {edition.one_more_thing && (
            <div className="border border-border p-5 mt-6">
              <h2 className="text-[10px] tracking-[0.4em] uppercase text-muted-foreground mb-3 font-mono">
                One More Thing
              </h2>
              <p className="text-foreground/90 text-sm leading-relaxed">
                {edition.one_more_thing}
              </p>
              {(edition as any).one_more_thing_source && (
                <a
                  href={(edition as any).one_more_thing_source}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground hover:text-foreground tracking-wider uppercase mt-2 inline-block transition-colors"
                >
                  [Source →]
                </a>
              )}
            </div>
          )}

          {edition.kicker && (
            <div className="border-t border-border pt-5 mt-8">
              <p className="text-muted-foreground text-sm italic">{edition.kicker}</p>
            </div>
          )}

          {/* Previous / Next Edition Navigation */}
          {(prevEdition || nextEdition) && (
            <nav className="border-t border-border pt-6 mt-8 flex items-stretch justify-between gap-4" aria-label="Edition navigation">
              {prevEdition ? (
                <Link
                  to={`/edition/${prevEdition.slug || prevEdition.id}`}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors group min-w-0 flex-1"
                >
                  <ChevronLeft className="w-4 h-4 shrink-0 group-hover:-translate-x-0.5 transition-transform" />
                  <div className="min-w-0">
                    <span className="text-[10px] tracking-widest uppercase block">Previous</span>
                    {prevEdition.hook && (
                      <span className="text-xs font-display italic line-clamp-1 block">{prevEdition.hook}</span>
                    )}
                  </div>
                </Link>
              ) : <div />}
              {nextEdition ? (
                <Link
                  to={`/edition/${nextEdition.slug || nextEdition.id}`}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors group min-w-0 flex-1 justify-end text-right"
                >
                  <div className="min-w-0">
                    <span className="text-[10px] tracking-widest uppercase block">Next</span>
                    {nextEdition.hook && (
                      <span className="text-xs font-display italic line-clamp-1 block">{nextEdition.hook}</span>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 shrink-0 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              ) : <div />}
            </nav>
          )}
        </div>

        {/* CTA */}
        <div className="mt-12 border border-border p-8 text-center space-y-4">
          <p className="text-xs tracking-widest uppercase text-muted-foreground">
            Don't wait for yesterday's news. Get tomorrow's briefing on your schedule.
          </p>
          <Link to="/signup">
            <Button className="rounded-none text-xs tracking-widest uppercase px-10 py-5">
              Subscribe — It's free
            </Button>
          </Link>
        </div>
      </article>

      <div className="mt-8 max-w-2xl mx-auto px-4">
        <div className="border border-accent/30 rounded-sm p-6 text-center space-y-3">
          <p className="text-[10px] tracking-[0.4em] uppercase text-muted-foreground font-mono">
            ☕ Enjoying aibly? Support the project
          </p>
          <p className="text-muted-foreground text-sm">
            aibly is free, independent, and built for the curious — not advertisers.
          </p>
          <a href="https://jami.bio/nabiloo19/tip" target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm" className="rounded-none border-accent/50 text-accent hover:bg-accent hover:text-accent-foreground text-xs tracking-widest uppercase">
              ☕ Fuel the Mission
            </Button>
          </a>
        </div>
      </div>

      <footer className="border-t border-border py-8 px-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between text-xs text-muted-foreground">
          <img src={logoIcon} alt="aibly logo" width={500} height={500} className="h-8 w-auto dark:invert sm:hidden" />
          <img src={logoFull} alt="aibly logo" width={486} height={135} className="h-7 w-auto dark:invert hidden sm:block" />
          <span>AI news, daily, for free</span>
        </div>
      </footer>
    </div>
  );
};

export default PublicEdition;
